import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShoppingCart, Minus, Plus, Truck, ShieldCheck, RotateCcw, Maximize2,
  Check, Star, ChevronLeft, ChevronRight, Droplets, Award, Wrench, Heart,
  ClipboardList, Info,
} from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import api from "@/lib/api";
import Seo from "@/components/common/Seo";
import Reveal from "@/components/common/Reveal";
import ProductCard from "@/components/common/ProductCard";
import QuickView from "@/components/common/QuickView";
import RatingStars from "@/components/common/RatingStars";
import PaymentBadges from "@/components/common/PaymentBadges";
import { useCart } from "@/lib/cart";
import { formatINR } from "@/lib/format";
import { productLd, breadcrumbLd, faqLd, SITE_URL } from "@/lib/jsonld";
import { SEO_CONFIG } from "@/lib/seoConfig";
import { stagger, fadeUp } from "@/lib/motion";

const BENEFIT_ICONS = [Droplets, ShieldCheck, Sparkle, Check, Heart];
function Sparkle(props) { return <Star {...props} />; }

const BRAND_PROMISE = [
  { icon: Award, title: "Professional Grade", text: "Formulated to detailing-studio standards." },
  { icon: Wrench, title: "Made for Every Drive", text: "Daily commutes to weekend details." },
  { icon: Heart, title: "Trusted by Enthusiasts", text: "Loved by 10,000+ car owners." },
  { icon: ShieldCheck, title: "Engineered for Excellence", text: "Tested for real-world performance." },
];

// Base products that have a dedicated 5 L variant page (links to that product).
const FIVE_L_MAP = {
  "car-shampoo": "car-shampoo-5l-can",
  "dashboard-polish": "dashboard-polish-5l-can",
  "tyre-shine": "tyre-shine-5l-can",
  "waterless-wash": "waterless-wash-5l-can",
  "interior-cleaner": "interior-cleaner-5l-can",
  "premium-liquid-wax": "premium-liquid-wax-5l-can",
  "wheel-cleaner": "wheel-cleaner-5l-can",
  "windshield-wash": "windshield-wash-wiper-fluid-5l-can",
};

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [variantIdx, setVariantIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [zoom, setZoom] = useState(false);
  const [origin, setOrigin] = useState("50% 50%");
  const [quickView, setQuickView] = useState(null);
  const { addItem, setOpen } = useCart();
  const imgRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    window.scrollTo({ top: 0 });
    api
      .getProduct(slug)
      .then((d) => {
        setProduct(d);
        setActiveImg(0);
        setVariantIdx(0);
        setQty(1);
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="acx-container px-4 py-16 sm:px-6 lg:px-10">
        <div className="grid gap-10 lg:grid-cols-2">
          <div className="aspect-square animate-pulse rounded-[18px] bg-[var(--acx-card)]" />
          <div className="space-y-4">
            <div className="h-8 w-2/3 animate-pulse rounded bg-[var(--acx-card)]" />
            <div className="h-24 animate-pulse rounded bg-[var(--acx-card)]" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="acx-container px-4 py-32 text-center">
        <h1 className="font-display text-3xl font-bold">Product not found</h1>
        <Link to="/shop" className="mt-6 inline-block rounded-[12px] bg-white px-6 py-3 font-semibold text-black">Back to Shop</Link>
      </div>
    );
  }

  const variant = product.variants?.[variantIdx] || { label: "Default", price: product.price, mrp: product.mrp };
  const category = product.category;
  const onMove = (e) => {
    const rect = imgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setOrigin(`${x}% ${y}%`);
  };

  const catLabel = category ? category.charAt(0).toUpperCase() + category.slice(1) : "Shop";
  // FAQ grounded in facts already visible on this page (shipping/payment/returns badges).
  const productFaq = [
    { q: "Do you offer free shipping?", a: "Yes — free shipping on all orders above ₹999." },
    { q: "Are payments secure?", a: "Yes, all payments are 100% protected with secure checkout." },
    { q: "What is your return policy?", a: "Easy returns within a 7-day policy." },
  ];
  const jsonLd = [
    productLd(product),
    breadcrumbLd([
      { name: "Home", url: SITE_URL },
      { name: catLabel, url: `${SITE_URL}/shop?category=${category}` },
      { name: product.name, url: `${SITE_URL}/product/${product.slug}` },
    ]),
    faqLd(productFaq),
  ];

  return (
    <div className="bg-[var(--acx-bg)]">
      <Seo
        title={SEO_CONFIG[slug]?.title || product.name}
        description={SEO_CONFIG[slug]?.description || product.shortDescription}
        image={SEO_CONFIG[slug] ? undefined : product.images?.[0]}
        type="product"
        imageAlt={`${product.name} — AutoCareX`}
        jsonLd={jsonLd}
      />

      <div className="acx-container px-4 pt-8 sm:px-6 lg:px-10">
        <nav className="flex items-center gap-2 text-[13px] text-white/50">
          <Link to="/" className="hover:text-white">Home</Link><span>/</span>
          <Link to="/shop" className="hover:text-white">Shop</Link><span>/</span>
          <Link to={`/shop?category=${category}`} className="capitalize hover:text-white">{category}</Link><span>/</span>
          <span className="text-white/80">{product.name}</span>
        </nav>
      </div>

      {/* Main */}
      <div className="acx-container px-4 pb-12 pt-6 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,0.85fr)_1fr] lg:items-start">
          {/* Gallery */}
          <div data-testid="pdp-gallery">
            <div
              ref={imgRef}
              onMouseEnter={() => setZoom(true)}
              onMouseLeave={() => setZoom(false)}
              onMouseMove={onMove}
              className="group relative h-[320px] overflow-hidden rounded-[18px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] sm:h-[420px] lg:h-[460px]"
            >
              <img
                src={product.images[activeImg]}
                alt={product.name}
                style={{ transformOrigin: origin, transform: zoom ? "scale(1.7)" : "scale(1)" }}
                className="h-full w-full object-contain transition-transform duration-200 ease-out"
              />
              <button
                onClick={() => setFullscreen(true)}
                data-testid="pdp-gallery-fullscreen-button"
                className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-black/60 text-white backdrop-blur transition-colors hover:bg-black/80"
                aria-label="Fullscreen"
              >
                <Maximize2 size={16} />
              </button>
            </div>
            {product.images.length > 1 && (
              <div className="mt-4 flex gap-3">
                {product.images.map((img, i) => (
                  <button
                    key={img || i}
                    onClick={() => setActiveImg(i)}
                    className={`h-20 w-20 overflow-hidden rounded-[12px] border transition-all ${
                      i === activeImg ? "border-white" : "border-[color:var(--acx-border)] opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${i + 1}`} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Purchase panel */}
          <div>
            <span className="inline-block rounded-full border border-[color:var(--acx-border)] bg-white/5 px-3 py-1 text-[11px] uppercase tracking-[0.16em] text-white/70">
              {category} Care
            </span>
            <h1 className="mt-4 font-display text-3xl font-bold tracking-[-0.02em] sm:text-4xl">{product.name}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <RatingStars value={product.rating} size={16} />
                <span className="text-[13px] text-white/70">{product.rating}</span>
              </div>
              <Link to="/reviews" className="text-[13px] text-white/55 underline-offset-2 hover:text-white hover:underline">
                {product.reviewCount} reviews
              </Link>
              <span className="text-[13px] text-white/45">{product.boughtPastMonth}+ bought in the past month</span>
            </div>

            <div className="mt-5 flex items-baseline gap-3">
              <span className="font-display text-3xl font-bold">{formatINR(variant.price)}</span>
              {variant.mrp > variant.price && (
                <>
                  <span className="text-[16px] text-white/40 line-through">{formatINR(variant.mrp)}</span>
                  <span className="rounded-md bg-[#9CA3AF]/15 px-2 py-0.5 text-[13px] font-semibold text-[#9CA3AF]">
                    {Math.round((1 - variant.price / variant.mrp) * 100)}% off
                  </span>
                </>
              )}
            </div>
            <p className="mt-5 text-[15px] leading-relaxed text-white/65">{product.description}</p>

            {/* Variants */}
            <div className="mt-6">
              <p className="mb-2 text-[12px] uppercase tracking-[0.16em] text-white/50">Size</p>
              <div className="flex flex-wrap gap-2.5" data-testid="pdp-variant-selector">
                {product.variants.map((v, i) => (
                  <button
                    key={v.label}
                    onClick={() => setVariantIdx(i)}
                    data-testid={`pdp-variant-${i}`}
                    className={`rounded-[12px] border px-4 py-2.5 text-[14px] font-medium transition-colors ${
                      i === variantIdx ? "border-white bg-white text-black" : "border-[color:var(--acx-border)] text-white/80 hover:border-white/30"
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
                {FIVE_L_MAP[product.slug] && (
                  <Link
                    to={`/product/${FIVE_L_MAP[product.slug]}`}
                    data-testid="pdp-5l-size-link"
                    title="View the 5 L size"
                    className="inline-flex items-center rounded-[12px] border border-[color:var(--acx-border)] px-4 py-2.5 text-[14px] font-medium text-white/80 transition-colors hover:border-white/30 hover:text-white"
                  >
                    5 L
                  </Link>
                )}
              </div>
            </div>

            {/* Quantity + actions */}
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <div className="inline-flex w-max items-center self-start rounded-[12px] border border-[color:var(--acx-border)] sm:self-auto" data-testid="pdp-quantity-stepper">
                <button className="px-3.5 py-3 text-white/70 hover:text-white" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease"><Minus size={16} /></button>
                <span className="w-10 text-center text-[15px]">{qty}</span>
                <button className="px-3.5 py-3 text-white/70 hover:text-white" onClick={() => setQty((q) => q + 1)} aria-label="Increase"><Plus size={16} /></button>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:contents">
                <button
                  data-testid="pdp-add-to-cart-button"
                  onClick={() => addItem(product, variant, qty)}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[12px] border border-[color:var(--acx-border)] bg-white/5 px-4 py-3.5 text-[14px] font-semibold text-white transition-colors hover:bg-white/10 sm:flex-1 sm:px-6 sm:text-[15px]"
                >
                  <ShoppingCart size={17} /> Add to Cart
                </button>
                <button
                  data-testid="pdp-buy-now-button"
                  onClick={() => { addItem(product, variant, qty); setOpen(true); }}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[12px] bg-[#E11B22] px-4 py-3.5 text-[14px] font-semibold text-white transition-colors hover:bg-[#c9151b] sm:flex-1 sm:px-6 sm:text-[15px]"
                >
                  Buy Now
                </button>
              </div>
            </div>

            {/* Trust badges */}
            <div className="mt-6 grid grid-cols-3 gap-3">
              {[{ i: Truck, t: "Free Shipping", s: "Orders above ₹999" }, { i: ShieldCheck, t: "Secure Payment", s: "100% protected" }, { i: RotateCcw, t: "Easy Returns", s: "7-day policy" }].map((b) => (
                <div key={b.t} className="rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-3 text-center">
                  <b.i size={18} className="mx-auto text-white/70" />
                  <p className="mt-2 text-[12px] font-semibold text-white">{b.t}</p>
                  <p className="text-[11px] text-white/45">{b.s}</p>
                </div>
              ))}
            </div>

            {/* Payment methods & COD */}
            <div className="mt-4 rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-3.5">
              <p className="mb-2.5 text-[11px] uppercase tracking-[0.16em] text-white/45">Secure checkout — pay your way</p>
              <PaymentBadges showCod />
            </div>
          </div>
        </div>
      </div>

      {/* Benefits grid */}
      <section className="border-t border-[color:var(--acx-border)] bg-[var(--acx-bg-2)]">
        <div className="acx-container px-4 py-16 sm:px-6 lg:px-10">
          <Reveal><h2 className="font-display text-3xl font-bold tracking-[-0.02em] sm:text-4xl">Why you'll love it</h2></Reveal>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger} className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-3" data-testid="pdp-benefits-grid">
            {product.benefits.map((b, i) => {
              const Icon = BENEFIT_ICONS[i % BENEFIT_ICONS.length];
              return (
                <motion.div key={b} variants={fadeUp} className="flex items-center gap-3 rounded-[14px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-5">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E11B22]/15 text-[#E11B22]"><Icon size={18} /></span>
                  <span className="text-[14px] font-medium text-white">{b}</span>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Description, How to use & Specifications */}
      {(product.longDescription || product.howToUse?.length > 0 || product.specifications?.length > 0) && (
        <section className="border-t border-[color:var(--acx-border)] bg-[var(--acx-bg)]">
          <div className="acx-container px-4 py-16 sm:px-6 lg:px-10">
            {product.longDescription && (
              <Reveal>
                <div className="max-w-3xl" data-testid="pdp-long-description">
                  <p className="text-[12px] uppercase tracking-[0.22em] text-white/50">Product Description</p>
                  <h2 className="mt-3 font-display text-3xl font-bold tracking-[-0.02em] sm:text-4xl">About this product</h2>
                  <p className="mt-5 text-[15px] leading-relaxed text-white/65">{product.longDescription}</p>
                </div>
              </Reveal>
            )}

            <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-2">
              {product.howToUse?.length > 0 && (
                <Reveal>
                  <div data-testid="pdp-how-to-use">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#E11B22]/15 text-[#E11B22]"><ClipboardList size={18} /></span>
                      <h3 className="font-display text-2xl font-bold tracking-[-0.02em]">How to use</h3>
                    </div>
                    <ol className="mt-6 space-y-3">
                      {product.howToUse.map((step, i) => (
                        <li key={i} className="flex gap-4 rounded-[14px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-4" data-testid={`pdp-how-to-use-step-${i}`}>
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-[14px] font-bold text-black">{i + 1}</span>
                          <div>
                            {step.title && <p className="text-[14px] font-semibold text-white">{step.title}</p>}
                            <p className="mt-0.5 text-[13px] leading-relaxed text-white/60">{step.text}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                </Reveal>
              )}

              {product.specifications?.length > 0 && (
                <Reveal>
                  <div data-testid="pdp-specifications">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--acx-border)] bg-white/5 text-white/80"><Info size={18} /></span>
                      <h3 className="font-display text-2xl font-bold tracking-[-0.02em]">Specifications</h3>
                    </div>
                    <dl className="mt-6 overflow-hidden rounded-[14px] border border-[color:var(--acx-border)]">
                      {product.specifications.map(([label, value], i) => (
                        <div key={label || i} className={`flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6 ${i % 2 === 0 ? "bg-[var(--acx-card)]" : "bg-[var(--acx-bg-2)]"}`}>
                          <dt className="text-[12px] uppercase tracking-[0.12em] text-white/45">{label}</dt>
                          <dd className="text-[14px] font-medium text-white sm:text-right">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </Reveal>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Perfect wash story */}
      <section className="relative acx-noise overflow-hidden border-t border-[color:var(--acx-border)] bg-[var(--acx-bg)]">
        <div className="acx-container grid grid-cols-1 items-center gap-10 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:px-10">
          <Reveal>
            <p className="text-[12px] uppercase tracking-[0.22em] text-white/50">The AutoCareX difference</p>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-[-0.02em] sm:text-5xl">{product.storyHeading || "The perfect wash starts here."}</h2>
            <p className="mt-5 max-w-lg text-[16px] leading-relaxed text-white/60">
              {product.storyBody || "Every AutoCareX formula is engineered to deliver professional results at home — protecting your paint, enhancing gloss and making maintenance effortless drive after drive."}
            </p>
            <Link to="/about" className="mt-7 inline-flex items-center gap-2 rounded-[12px] border border-[color:var(--acx-border)] bg-white/5 px-6 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-white/10">
              Learn about our brand <ChevronRight size={16} />
            </Link>
          </Reveal>
          <Reveal>
            <div className="overflow-hidden rounded-[18px] border border-[color:var(--acx-border)] bg-black">
              <img src={product.images[1] || product.images[0]} alt="AutoCareX detailing" className="mx-auto h-[420px] w-full object-contain" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Brand promise */}
      <section className="border-t border-[color:var(--acx-border)] bg-[var(--acx-bg-2)]">
        <div className="acx-container grid grid-cols-2 gap-5 px-4 py-14 sm:px-6 lg:grid-cols-4 lg:px-10">
          {BRAND_PROMISE.map((b) => (
            <div key={b.title} className="text-center">
              <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full border border-[color:var(--acx-border)] bg-white/5 text-white/80"><b.icon size={20} /></span>
              <p className="mt-3 text-[14px] font-semibold text-white">{b.title}</p>
              <p className="mt-1 text-[12px] text-white/50">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Related */}
      {product.related?.length > 0 && (
        <section className="border-t border-[color:var(--acx-border)] bg-[var(--acx-bg)]">
          <div className="acx-container px-4 py-16 sm:px-6 lg:px-10">
            <h2 className="mb-8 font-display text-3xl font-bold tracking-[-0.02em] sm:text-4xl">You may also like</h2>
            <div className="no-scrollbar -mx-4 flex gap-5 overflow-x-auto px-4 pb-2" data-testid="pdp-related-products">
              {product.related.map((p) => (
                <div key={p.id} className="w-[78%] shrink-0 sm:w-[44%] lg:w-[24%]">
                  <ProductCard product={p} onQuickView={setQuickView} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Fullscreen */}
      <Dialog open={fullscreen} onOpenChange={setFullscreen}>
        <DialogContent className="max-w-4xl border-[color:var(--acx-border)] bg-black p-2">
          <div className="relative">
            <img src={product.images[activeImg]} alt={product.name} className="max-h-[80vh] w-full rounded-lg object-contain" />
            <div className="mt-3 flex justify-center gap-2">
              <button onClick={() => setActiveImg((i) => (i - 1 + product.images.length) % product.images.length)} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white"><ChevronLeft size={18} /></button>
              <button onClick={() => setActiveImg((i) => (i + 1) % product.images.length)} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white"><ChevronRight size={18} /></button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <QuickView product={quickView} open={!!quickView} onOpenChange={(o) => !o && setQuickView(null)} />
    </div>
  );
}
